import { Upload } from "@mui/icons-material"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import QrCodeIcon from "@mui/icons-material/QrCode"
import RestoreIcon from "@mui/icons-material/Restore"
import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridPaginationModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridValidRowModel,
} from "@mui/x-data-grid"
import { deDE } from "@mui/x-data-grid/locales"
import { StyledButton } from "components/Button/StyledButton"
import { useEffect, useMemo, useState } from "react"

interface DataGridComponentProps<T extends GridValidRowModel>
  extends Omit<DataGridProps, "rows" | "columns" | "getRowId"> {
  columns: GridColDef[]
  rows: T[]
  getRowId: (row: T) => string
  onEdit?: (id: string) => void
  onAdd?: () => void
  onDelete?: (selectedIds: string[]) => void
  onCopy?: (id: string) => void
  title?: string
  isNoToolbar?: boolean
  importButton?: boolean
  importFunction?: () => void
  onQRCode?: (row: T) => void
  noDelete?: boolean
  enablePagination?: boolean
  initialPageSize?: number
  pageSizeOptions?: number[]
  canEditRow?: (row: T) => boolean
  canDeleteRow?: (row: T) => boolean
  onRestore?: (id: string) => void
  isRowDeleted?: (row: T) => boolean
}

const DataGridComponent = <T extends GridValidRowModel>({
  columns,
  rows,
  getRowId,
  onEdit,
  onAdd,
  onDelete,
  onCopy,
  title,
  onQRCode,
  isNoToolbar = false,
  noDelete = false,
  enablePagination = true,
  initialPageSize,
  pageSizeOptions,
  canEditRow,
  canDeleteRow,
  onRestore,
  isRowDeleted,
  ...dataGridProps
}: DataGridComponentProps<T>) => {
  const [forceDeleteEnabled, setForceDeleteEnabled] = useState(false)
  const resolvedPageSizeOptions = useMemo(() => {
    if (pageSizeOptions && pageSizeOptions.length > 0) {
      return pageSizeOptions
    }
    return [25, 50, 100]
  }, [pageSizeOptions])
  const defaultPageSize = initialPageSize ?? resolvedPageSizeOptions[0]
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: defaultPageSize,
  })

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "F7") {
        event.preventDefault()
        setForceDeleteEnabled((prev) => !prev)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  useEffect(() => {
    if (!enablePagination) {
      return
    }

    setPaginationModel((prev) => {
      const fallbackPageSize =
        initialPageSize ??
        (resolvedPageSizeOptions.includes(prev.pageSize)
          ? prev.pageSize
          : resolvedPageSizeOptions[0])
      const nextPageSize = fallbackPageSize
      const maxPage = Math.max(0, Math.ceil(rows.length / nextPageSize) - 1)
      const nextPage = prev.page > maxPage ? maxPage : prev.page

      if (nextPage === prev.page && nextPageSize === prev.pageSize) {
        return prev
      }

      return {
        page: nextPage,
        pageSize: nextPageSize,
      }
    })
  }, [rows.length, enablePagination, initialPageSize, resolvedPageSizeOptions])

  const shouldShowDelete = !noDelete || forceDeleteEnabled

  return (
    <Box style={{ maxHeight: "calc(100vh - 160px)", width: "100%" }}>
      {title && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          {title}
        </Typography>
      )}
      <DataGrid
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        getRowId={getRowId}
        rows={rows}
        getRowHeight={() => "auto"}
        columns={[
          ...columns,
          {
            field: "actions",
            headerName: "Aktionen",
            type: "actions",
            width: 150,
            renderCell: (params) => (
              <>
                {onEdit && (!canEditRow || canEditRow(params.row)) && (
                  <StyledButton
                    buttonType="edit"
                    iconOnly={true}
                    onClick={() => onEdit(getRowId(params.row))}
                    size="small"
                  />
                )}
                {shouldShowDelete &&
                  onDelete &&
                  (!canDeleteRow || canDeleteRow(params.row)) &&
                  !(onRestore && isRowDeleted && isRowDeleted(params.row)) && (
                    <StyledButton
                      buttonType="delete"
                      iconOnly={true}
                      onClick={() => onDelete([getRowId(params.row)])}
                      size="small"
                    />
                  )}
                {onRestore && isRowDeleted && isRowDeleted(params.row) && (
                  <Tooltip title="Wiederherstellen">
                    <span>
                      <StyledButton
                        buttonType="custom"
                        customIcon={<RestoreIcon fontSize="small" />}
                        iconOnly={true}
                        onClick={() => onRestore(getRowId(params.row))}
                        size="small"
                      />
                    </span>
                  </Tooltip>
                )}
                {onCopy && (
                  <Tooltip title="Kopieren">
                    <span>
                      <StyledButton
                        buttonType="custom"
                        customIcon={<ContentCopyIcon fontSize="small" />}
                        iconOnly={true}
                        onClick={() => onCopy(getRowId(params.row))}
                        size="small"
                      />
                    </span>
                  </Tooltip>
                )}
                {onQRCode !== undefined && onQRCode && (
                  <IconButton size="small" onClick={() => onQRCode(params.row)}>
                    <QrCodeIcon fontSize="small" />
                  </IconButton>
                )}
              </>
            ),
          },
        ]}
        checkboxSelection={false}
        hideFooterPagination={!enablePagination}
        {...(enablePagination
          ? {
              pagination: true,
              paginationModel,
              onPaginationModelChange: (model: GridPaginationModel) => setPaginationModel(model),
              pageSizeOptions: resolvedPageSizeOptions,
            }
          : {})}
        slots={{
          toolbar: () => (
            <GridToolbarContainer>
              {!isNoToolbar && (
                <>
                  <GridToolbarColumnsButton />
                  <GridToolbarFilterButton />
                  <GridToolbarDensitySelector />
                </>
              )}
              {onAdd && (
                <StyledButton buttonType="add" onClick={onAdd}>
                  Hinzufügen
                </StyledButton>
              )}
              {dataGridProps.importButton && dataGridProps.importFunction ? (
                <StyledButton
                  buttonType="custom"
                  customIcon={<Upload fontSize="small" />}
                  onClick={dataGridProps.importFunction}
                  size="small"
                >
                  Importieren
                </StyledButton>
              ) : null}
              <GridToolbarQuickFilter />
            </GridToolbarContainer>
          ),
        }}
        {...dataGridProps}
      />
    </Box>
  )
}

export default DataGridComponent
